import {
  type ReactElement,
  type SyntheticEvent,
  useEffect,
  useState
} from "react";
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ItemValue, QuestionCheckboxModel } from "survey-core";
import GLTooltip from "components/v3/sharedComponents/GLTooltip";

export type ChoicePillLayout = "stack" | "wrap";

type ChoiceQuestion = {
  visibleChoices: ItemValue[];
  isItemSelected: (item: ItemValue) => boolean;
  clickItemHandler: (item: ItemValue, checked?: boolean) => void;
  isReadOnly?: boolean;
};

type ChoicePillListProps = {
  question: ChoiceQuestion;
  multi?: boolean;
  layout?: ChoicePillLayout;
};

const getChoiceTooltip = (item: ItemValue): string => {
  const tooltip = (item as ItemValue & { tooltip?: string }).tooltip;
  return typeof tooltip === "string" ? tooltip : "";
};

const ChoicePillList = ({
  question,
  multi = false,
  layout = "stack"
}: ChoicePillListProps): ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"), { noSsr: true });
  const [openTooltipKey, setOpenTooltipKey] = useState<string | null>(null);
  const isWrapLayout = layout === "wrap";

  useEffect(() => {
    if (!isMobile || !openTooltipKey) {
      return;
    }

    const closeOnTapAnywhere = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(".gl-choice-pill__info-btn")
      ) {
        return;
      }
      setOpenTooltipKey(null);
    };

    document.addEventListener("pointerdown", closeOnTapAnywhere, true);
    return () => {
      document.removeEventListener("pointerdown", closeOnTapAnywhere, true);
    };
  }, [isMobile, openTooltipKey]);
  const checkboxQuestion = multi
    ? (question as ChoiceQuestion & QuestionCheckboxModel)
    : null;
  const showSelectAll = Boolean(checkboxQuestion?.showSelectAllItem);
  const selectAllItem = checkboxQuestion?.selectAllItem;
  const choiceItems = selectAllItem
    ? question.visibleChoices.filter((item) => item !== selectAllItem)
    : question.visibleChoices;

  const handleSelect = (item: ItemValue) => {
    if (question.isReadOnly) return;
    if (multi) {
      question.clickItemHandler(item, !question.isItemSelected(item));
    } else {
      question.clickItemHandler(item);
    }
  };

  const handleSelectAll = () => {
    if (question.isReadOnly || !checkboxQuestion) return;
    checkboxQuestion.toggleSelectAll();
  };

  const renderPill = (
    item: ItemValue,
    {
      onSelect,
      selected,
      selectAll = false
    }: {
      onSelect: () => void;
      selected: boolean;
      selectAll?: boolean;
    }
  ) => {
    const tooltip = getChoiceTooltip(item);
    const itemKey = String(item.value);
    const isTooltipOpen = openTooltipKey === itemKey;
    const handleTooltipClose = (event?: Event | SyntheticEvent) => {
      if (isMobile && event) {
        const path =
          "composedPath" in event && event.composedPath
            ? event.composedPath()
            : [];
        const clickedTrigger = path.some(
          (node) =>
            node instanceof HTMLElement &&
            node.classList.contains("gl-choice-pill__info-btn")
        );
        if (clickedTrigger) {
          return;
        }
      }
      setOpenTooltipKey(null);
    };

    return (
      <Box
        key={itemKey}
        className={`gl-choice-pill${selected ? " gl-choice-pill--selected" : ""}${
          selectAll ? " gl-choice-pill--select-all" : ""
        }`}
      >
        <Box
          className="gl-choice-pill__main"
          component="button"
          type="button"
          disabled={question.isReadOnly}
          aria-pressed={selected}
          onClick={onSelect}
        >
          <Box
            className={`gl-choice-pill__check${
              selected ? " gl-choice-pill__check--visible" : ""
            }`}
            aria-hidden="true"
          />
          <Typography className="gl-choice-pill__label" component="span">
            {item.text || itemKey}
          </Typography>
        </Box>

        {tooltip ? (
          <Box className="gl-choice-pill__info-wrap">
            <GLTooltip
              disableFocusListener={isMobile}
              disableHoverListener={isMobile}
              disableTouchListener={isMobile}
              open={isTooltipOpen}
              onClose={handleTooltipClose}
              onOpen={() => {
                if (!isMobile) {
                  setOpenTooltipKey(itemKey);
                }
              }}
              placement="left-start"
              PopperProps={{
                modifiers: [
                  { name: "flip", enabled: false },
                  {
                    name: "preventOverflow",
                    options: {
                      altAxis: true,
                      boundary: "viewport",
                      padding: 16,
                      tether: true
                    }
                  }
                ]
              }}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: isMobile ? "calc(100vw - 32px)" : 250
                  }
                }
              }}
              title={
                <Box
                  component="span"
                  dangerouslySetInnerHTML={{ __html: tooltip }}
                  sx={{
                    display: "block",
                    "& strong": { fontWeight: 700 }
                  }}
                />
              }
            >
              <IconButton
                className="gl-choice-pill__info-btn"
                aria-label={`More information about ${item.text || itemKey}`}
                size="small"
                sx={{ p: 0 }}
                onClick={
                  isMobile
                    ? (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setOpenTooltipKey((current) =>
                          current === itemKey ? null : itemKey
                        );
                      }
                    : undefined
                }
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </GLTooltip>
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <Box
      className={`gl-choice-pill-list${
        isWrapLayout ? " gl-choice-pill-list--wrap" : ""
      }`}
    >
      {showSelectAll && selectAllItem
        ? renderPill(selectAllItem, {
            onSelect: handleSelectAll,
            selected: checkboxQuestion?.isAllSelected ?? false,
            selectAll: !isWrapLayout
          })
        : null}
      {choiceItems.map((item) =>
        renderPill(item, {
          onSelect: () => handleSelect(item),
          selected: question.isItemSelected(item)
        })
      )}
    </Box>
  );
};

export default ChoicePillList;
